import {
  Component,
  input,
  output,
  OnInit,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SHARED_IMPORTS } from '../../core/shared-imports';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DynamicFormConfig, FormField, FormValue } from './dynamic-form.model';
import { tenant } from '../../../tenants/tenant';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

@Component({
  selector: 'nexa-dynamic-form',
  imports: [
    ...SHARED_IMPORTS,
    InputTextModule,
    PasswordModule,
    CheckboxModule,
    RadioButtonModule,
    SelectModule,
    MultiSelectModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    ToggleSwitchModule,
  ],
  templateUrl: './dynamic-form.html',
  styleUrl: './dynamic-form.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicForm implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);

  config = input.required<DynamicFormConfig>();
  initialValues = input<FormValue>({});
  disabled = input<boolean>(false);

  formSubmit = output<FormValue>();
  formChange = output<FormValue>();
  formReset = output<void>();
  actionLinkClick = output<string>();

  form!: FormGroup;
  formValueSignal = signal<FormValue>({});
  disabledKeysSignal = signal<ReadonlySet<string>>(new Set<string>());
  private subscriptions = new Subscription();
  iconColor = computed(() => this.config().iconColor || tenant.theme.semantic?.form?.iconColor || '#003B55');
  isFormValid = computed(() => {
    this.formValueSignal(); 
    return this.form && this.form.valid;
  });
  
  isFormDirty = computed(() => {
    this.formValueSignal();
    return this.form && this.form.dirty;
  });
  
  submitButtonColor = computed(() => {
    if (this.isFormValid()) {
      return this.config().submitButtonColor || tenant.theme.semantic?.form?.iconColor || '#003B55';
    }
    return undefined; 
  });
  
  sortedFields = computed(() => {
    const fields = this.config().fields.filter((f) => !f.hidden);
    return [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) {
    effect(() => {
      this.formValueSignal();
    }, { allowSignalWrites: true });
    
    effect(() => {
      const initialVals = this.initialValues();
      if (this.form && Object.keys(initialVals).length > 0) {
        this.form.patchValue(initialVals);
        this.form.markAsPristine();
        this.form.markAsUntouched();
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const config = this.config();
      if (this.form) {
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
        this.buildForm();
        this.setInitialValues();
        this.setupChangeListeners();
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.buildForm();
    this.setInitialValues();
    this.setupChangeListeners();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupChangeListeners(): void {
    const emitOnChange = this.config().emitOnChange !== false; 
    const emitOnlyWhenValid = this.config().emitOnlyWhenValid ?? false;
    
    const formSub = this.form.valueChanges.subscribe(() => {
      this.formValueSignal.set(this.form.value);
      
      if (emitOnChange && (!emitOnlyWhenValid || this.form.valid)) {
        this.formChange.emit(this.form.value);
      }
    });
    this.subscriptions.add(formSub);

    const statusSub = this.form.statusChanges.subscribe(() => {
      this.formValueSignal.set(this.form.value);
      this.cdr.markForCheck();
    });
    this.subscriptions.add(statusSub);

    this.config().fields.forEach((field) => {
      if (field.onChange) {
        const control = this.form.get(field.key);
        if (control) {
          const fieldSub = control.valueChanges.subscribe((value) => {
            field.onChange!(value, field, this.form.value);
          });
          this.subscriptions.add(fieldSub);
        }
      }
    });
  }

  private buildForm(): void {
    const group: { [key: string]: any } = {};
    const initialValues = this.initialValues();

    this.config().fields.forEach((field) => {
      const validators = this.buildValidators(field);
      let defaultValue: any;
      
      const hasInitialValue = field.key in initialValues && 
                             initialValues[field.key] !== null && 
                             (Array.isArray(initialValues[field.key]) || initialValues[field.key] !== '');
      
      if (hasInitialValue) {
        defaultValue = initialValues[field.key];
      } else if (field.type === 'checkbox' || field.type === 'toggle') {
        defaultValue = field.defaultValue ?? false;
      } else if (field.type === 'select' && field.multiselect) {
        defaultValue = field.defaultValue ?? [];
      } else if (field.type === 'select') {
        defaultValue = field.defaultValue ?? null;
      } else {
        defaultValue = field.defaultValue ?? '';
      }
      
      group[field.key] = [
        { value: defaultValue, disabled: field.disabled || this.disabled() },
        validators,
      ];
    });

    this.form = this.fb.group(group);
  }

  private buildValidators(field: FormField): any[] {
    const validators: any[] = [];

    if (field.required) {
      validators.push(Validators.required);
    }

    if (field.validation) {
      const val = field.validation;
      if (val.min !== undefined) {
        validators.push(Validators.min(val.min));
      }
      if (val.max !== undefined) {
        validators.push(Validators.max(val.max));
      }
      if (val.minLength !== undefined) {
        validators.push(Validators.minLength(val.minLength));
      }
      if (val.maxLength !== undefined) {
        validators.push(Validators.maxLength(val.maxLength));
      }
      if (val.pattern) {
        validators.push(Validators.pattern(val.pattern));
      }
    }

    if (field.type === 'email') {
      validators.push(Validators.email);
    }

    return validators;
  }

  private setInitialValues(): void {
    const initialValues = this.initialValues();
    if (Object.keys(initialValues).length > 0) {
      this.form.patchValue(initialValues);
    }
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.formSubmit.emit(this.form.value);
    } else {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
    }
  }

  onReset(): void {
    const defaultValues: { [key: string]: any } = {};
    this.config().fields.forEach((field) => {
      if (field.type === 'checkbox') {
        defaultValues[field.key] = false;
      } else if (field.type === 'select' && field.multiselect) {
        defaultValues[field.key] = field.defaultValue ?? [];
      } else {
        defaultValues[field.key] = field.defaultValue ?? '';
      }
    });

    this.form.reset(defaultValues);
    this.setInitialValues();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.formReset.emit();
  }

  getFormValue(): FormValue {
    return this.form.value;
  }

  getFormGroup(): FormGroup {
    return this.form;
  }

  setFieldDisabled(key: string, disabled: boolean): void {
    if (!this.form) return;
    const control = this.form.get(key);
    if (!control) return;
    if (disabled) {
      control.disable();
    } else {
      control.enable();
    }
    this.disabledKeysSignal.update((prev) => {
      const next = new Set(prev);
      if (disabled) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }

  getFieldError(fieldKey: string): string | null {
    const control = this.form.get(fieldKey);
    if (!control || !control.errors || !control.touched) {
      return null;
    }

    const errors = control.errors;
    const field = this.config().fields.find((f) => f.key === fieldKey);

    if (errors['required']) {
      return field?.errorMessage || this.translate.instant('FORM.REQUIRED', { field: field?.label || fieldKey });
    }
    if (errors['custom']) {
      return errors['custom'] as string;
    }
    if (errors['email']) {
      return this.translate.instant('FORM.INVALID_EMAIL');
    }
    if (errors['min']) {
      return this.translate.instant('FORM.MIN_VALUE', { min: errors['min'].min });
    }
    if (errors['max']) {
      return this.translate.instant('FORM.MAX_VALUE', { max: errors['max'].max });
    }
    if (errors['minlength']) {
      return this.translate.instant('FORM.MIN_LENGTH', { n: errors['minlength'].requiredLength });
    }
    if (errors['maxlength']) {
      return this.translate.instant('FORM.MAX_LENGTH', { n: errors['maxlength'].requiredLength });
    }
    if (errors['pattern']) {
      return this.translate.instant('FORM.INVALID_FORMAT');
    }

    return null;
  }

  getFieldControl(fieldKey: string): AbstractControl | null {
    return this.form.get(fieldKey);
  }

  isFieldInvalid(fieldKey: string): boolean {
    const control = this.form.get(fieldKey);
    return !!(control && control.invalid && control.touched);
  }

  getGridColClass(span?: number): string {
    if (!span || span === 1) return 'col-span-1';
    const maxSpan = Math.min(span, 12);
    if (maxSpan === 12) {
      return 'col-span-1 sm:col-span-12';
    } else {
      return `col-span-1 sm:col-span-${maxSpan}`;
    }
  }

  getLayoutClass(): string {
    const layout = this.config().layout || 'vertical';
    const columns = this.config().columns || 1;
    switch (layout) {
      case 'horizontal':
        return 'flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-4';
      case 'grid':
        if (columns === 1) {
          return 'grid grid-cols-1 gap-3 sm:gap-4 items-center';
        } else if (columns === 2) {
          return 'grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 items-center';
        } else {
          return `grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${Math.min(columns, 3)} lg:grid-cols-${columns} gap-3 sm:gap-4 items-center`;
        }
      default:
        return 'flex flex-col gap-3 sm:gap-4';
    }
  }

  clearField(fieldKey: string): void {
    const control = this.form.get(fieldKey);
    if (control) {
      control.setValue('');
      control.markAsTouched();
    }
  }

  hasValue(fieldKey: string): boolean {
    const formValue = this.formValueSignal();
    const value = formValue[fieldKey];
    return !!(value && value.toString().trim().length > 0);
  }
}
